<template>
  <div class="dashboard-view">
    <page-header :title="tr('Dashboard')">
      <template #buttons>
        <ui-button @click="() => openSettings()">
          {{ tr('Customize View') }}
        </ui-button>
      </template>
    </page-header>

    <system-level-notifications space-top class="mb-5" />

    <div v-if="$root.loggedInUser" class="flex flex-col gap-5 pb-5">
      <div class="mt-5 flex flex-col gap-3 sm:flex-row">
        <brand-filter v-model="brandId" :disabled="isLoading" class="sm:flex-1" />
        <date-range-filter v-model="dateFilter" :disabled="isLoading" />
      </div>

      <div class="grid grid-cols-2 gap-5 lg:grid-cols-5">
        <div
          v-for="tile in tiles"
          :key="tile.key"
          :class="[ 'glass-panel relative flex min-h-36 flex-col justify-between gap-4 rounded-xl p-5', tile.key === 'savings' && 'col-span-2 lg:col-span-1' ]"
        >
          <div class="flex items-start justify-between gap-2">
            <h2 class="m-0 text-sm leading-snug font-semibold text-foreground">
              {{ tile.label }}
            </h2>
            <component :is="tile.icon" class="size-4.5 shrink-0 text-muted-foreground" />
          </div>

          <ui-skeleton v-if="isLoading" class="h-10 w-16" />
          <div v-else class="flex flex-col">
            <span class="text-4xl leading-none font-bold tracking-tight text-foreground tabular-nums">{{ tile.value }}</span>
            <span v-if="tile.hint" class="mt-1 text-xs font-semibold text-muted-foreground">{{ tile.hint }}</span>
          </div>

          <arrow-link
            v-if="tile.link !== undefined && !isLoading"
            :to="tile.link"
            :label="tile.label"
            class="absolute right-4 bottom-4"
            @click="() => tile.onClick?.()"
          />
        </div>
      </div>

      <div class="grid gap-5 md:grid-cols-2">
        <dashboard-dual-chart
          :is-loading="isLoading"
          :date-from="dateFilter.start"
          :date-to="dateFilter.end"
          :dataset1="dashboardData ? getChartData(renderableMetrics[0].machine_name) : []"
          :dataset1-label="tr(renderableMetrics[0].label)"
          :dataset1-unit="renderableMetrics[0].is_currency ? currency : null"
          :dataset2="dashboardData ? getChartData(renderableMetrics[1].machine_name) : []"
          :dataset2-label="tr(renderableMetrics[1].label)"
          :dataset2-unit="renderableMetrics[1].is_currency ? currency : null"
          :chart-title="tr(renderableMetrics[0].label) + ' ' + tr('and') + ' ' + tr(renderableMetrics[1].label)"
        />

        <dashboard-dual-chart
          :is-loading="isLoading"
          :date-from="dateFilter.start"
          :date-to="dateFilter.end"
          :dataset1="dashboardData ? getChartData(renderableMetrics[2].machine_name) : []"
          :dataset1-label="tr(renderableMetrics[2].label)"
          :dataset1-unit="renderableMetrics[2].is_currency ? currency : null"
          :chart-title="tr(renderableMetrics[2].label)"
        />
      </div>

      <content-panel :title="tr('Waiting List')" :icon="ClockIcon">
        <rule-waiting-list-content
          :rule="null"
          is-rule-specific
          compact
          :number-of-items="5"
          :brand-id="brandId"
          disable-pager
        />

        <template #footer>
          <router-link to="/waiting-list" class="flex items-center gap-3 text-xs font-semibold text-foreground no-underline">
            {{ tr('View all waiting items') }}
            <arrow-link to="/waiting-list" :label="tr('View all waiting items')" />
          </router-link>
        </template>
      </content-panel>

      <div class="grid gap-5 md:grid-cols-2">
        <content-panel :title="tr('Latest post from Infinite∞Blog')" :icon="NewspaperIcon">
          <dashboard-blog-box />

          <template #footer>
            <a :href="blogUrl" target="_blank" class="flex items-center gap-3 text-xs font-semibold text-foreground no-underline">
              {{ tr('Read more from Infinite∞Blog') }}
              <arrow-link :to="blogUrl" :label="tr('Read more from Infinite∞Blog')" />
            </a>
          </template>
        </content-panel>

        <content-panel :title="tr('Your latest Activities')" :icon="HistoryIcon">
          <div class="-mx-4 max-h-[400px] overflow-y-auto px-4 [mask-image:linear-gradient(transparent,black_15px,black_calc(100%-15px),transparent)] [&_.event>.time>.date]:bg-card">
            <activity-log :brand-id="brandId" mobile />
          </div>
        </content-panel>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ChartColumn,
  CircleDollarSign,
  Clock,
  History,
  Megaphone,
  MousePointerClick,
  Newspaper,
  PiggyBank,
  Rocket,
  Users,
} from '@lucide/vue';
import { markRaw } from 'vue';

import DashboardMixin from '@/mixins/Dashboard.js';
import OnboardingAutoOpenMixin from '@/mixins/OnboardingAutoOpen.js';
import ActivityLog from '@/ui/components/ActivityLog.vue';
import ArrowLink from '@/ui/components/ArrowLink.vue';
import BrandFilter from '@/ui/components/BrandFilter.vue';
import ContentPanel from '@/ui/components/ContentPanel.vue';
import DashboardBlogBox from '@/ui/components/DashboardBlogBox.vue';
import DashboardDualChart from '@/ui/components/DashboardDualChart.vue';
import DashboardSettingsDialog from '@/ui/components/DashboardSettingsDialog.vue';
import DateRangeFilter from '@/ui/components/DateRangeFilter.vue';
import EstimatedSavingsDialog from '@/ui/components/EstimatedSavingsDialog.vue';
import PageHeader from '@/ui/components/PageHeader.vue';
import RuleWaitingListContent from '@/ui/components/RuleWaitingListContent.vue';
import ShoprenterSubscriptionSuccessModal from '@/ui/components/ShoprenterSubscriptionSuccessModal.vue';
import SystemLevelNotifications from '@/ui/components/SystemLevelNotifications.vue';
import { formatCompactNumber } from '@/utils/number';

export default {
  name: 'DashboardView',
  components: {
    ActivityLog,
    ArrowLink,
    BrandFilter,
    ContentPanel,
    DashboardBlogBox,
    DashboardDualChart,
    DateRangeFilter,
    RuleWaitingListContent,
    PageHeader,
    SystemLevelNotifications,
  },
  mixins: [ DashboardMixin, OnboardingAutoOpenMixin ],
  data: () => ({
    ClockIcon: markRaw(Clock),
    HistoryIcon: markRaw(History),
    NewspaperIcon: markRaw(Newspaper),
  }),
  computed: {
    currency() {
      const dashboardSettings = this.$root.loggedInUser?.dashboard_settings;
      if (!dashboardSettings) {
        return null;
      }

      if (dashboardSettings.currency === 'auto') {
        return this.dashboardData.currency ?? null;
      }

      return dashboardSettings.currency;
    },
    renderableMetrics() {
      return this.$root.loggedInUser?.dashboard_settings?.metrics ?? [];
    },
    tiles() {
      const metrics = this.dashboardData?.metrics;
      const savings = this.dashboardData?.estimated_savings;

      return [
        {
          key: 'automations',
          label: this.tr('Active automations'),
          icon: Rocket,
          value: metrics ? (metrics.active_rules || '–') : '–',
          link: '/automation',
          onClick: () => this.onNavigateToAutomation(),
        },
        {
          key: 'campaigns',
          label: this.tr('Active campaigns'),
          icon: Megaphone,
          value: metrics ? (metrics.active_campaigns || '–') : '–',
        },
        ...this.renderableMetrics.slice(0, 2).map(metric => ({
          key: 'metric-' + metric.machine_name,
          label: this.tr(metric.label) + (metric.is_currency && this.currency ? ' (' + this.currency + ')' : ''),
          icon: this.getMetricIcon(metric),
          value: this.getChartTotalValue(metric),
        })),
        {
          key: 'savings',
          label: this.tr('Savings') + (this.currency ? ' (' + this.currency + ')' : ''),
          icon: PiggyBank,
          value: savings?.total_amount ? formatCompactNumber(savings.total_amount) : '–',
          hint: savings?.manual_labor.total_minutes ? '+ ' + this.tr(':num min', { ':num': savings.manual_labor.total_minutes }) : '',
          link: savings ? null : undefined,
          onClick: () => this.onOpenSavingsDetails(),
        },
      ];
    },
    brandId: {
      get() {
        return this.$store.getters['dashboard/brandId'];
      },
      set(value) {
        this.$store.commit('dashboard/setBrandId', value);
      },
    },
    dateFilter: {
      get() {
        return this.$store.getters['dashboard/dateRange'];
      },
      set(value) {
        this.$store.commit('dashboard/setDateRange', value);
      },
    },
    blogUrl() {
      return this.$root.language === 'hu' ? 'https://infinite.tools/hu/blog/' : 'https://infinite.tools/blog/';
    },
  },
  watch: {
    brandId() {
      this.loadData();
    },
    dateFilter: {
      handler() {
        this.loadData();
      },
      deep: true,
    },
  },
  created() {
    if (
      !this.isLoading
      && Object.keys(this.dashboardData).length === 0
    ) {
      this.loadData();
    }

    this.$store.commit('rules/setBrandId', null);
  },
  mounted() {
    if (this.$route.redirectedFrom) {
      // eslint-disable-next-line no-console
      console.warn(`You arrived from an invalid route (${this.$route.redirectedFrom}), now you are on the /dashboard page.`);
    }

    if (this.$app.isShoprenterApp && this.$route.query.shoprenter_charge === 'success') {
      this.$fullscreenModal.open(
        this,
        ShoprenterSubscriptionSuccessModal,
        {},
        {
          closable: false,
          closeWithOutclick: false,
          componentListeners: {
            confirmed: () => {
              this.$router.replace('/dashboard');
              this.openOnboardingModal();
            },
          },
        },
      );
    }
  },
  methods: {
    loadData() {
      if (!this.$root.isLoggedIn) {
        return;
      }

      this.$store.dispatch('dashboard/fetch')
        .catch(err => this.error(err));
    },
    getChartDetails(metricMachineName) {
      if (!this.dashboardData || !this.dashboardData.metrics || !this.dashboardData.metrics.charts) {
        return null;
      }

      return this.dashboardData.metrics.charts.find(c => c.metric === metricMachineName) || null;
    },
    getChartData(metricMachineName) {
      const details = this.getChartDetails(metricMachineName);
      if (!details) {
        return [];
      }

      const dataset = [ ...details.data ];
      dataset.forEach(item => {
        if (item.date && typeof item.date === 'string') {
          item.date = new Date(item.date + 'T00:00:00Z');
        }
      });

      return dataset;
    },
    getChartTotalValue(metric) {
      const details = this.getChartDetails(metric.machine_name);
      if (!details) {
        return '–';
      }

      return formatCompactNumber(details.total_value);
    },
    getMetricIcon(metric) {
      if (metric.is_currency) {
        return CircleDollarSign;
      }
      if (metric.machine_name === 'impressions' || metric.machine_name === 'reach') {
        return Users;
      }
      if (metric.machine_name.includes('click') || metric.machine_name.includes('ctr')) {
        return MousePointerClick;
      }

      return ChartColumn;
    },
    openSettings() {
      this.$fullscreenModal.open(
        this,
        DashboardSettingsDialog,
        {
          onSettingsUpdated: () => this.loadData(),
        },
      );
    },
    onNavigateToAutomation() {
      this.$store.commit('rules/setBrandId', this.brandId ?? null);
    },
    onOpenSavingsDetails() {
      this.$fullscreenModal.open(
        this,
        EstimatedSavingsDialog,
        {
          estimate: this.dashboardData.estimated_savings,
          currency: this.currency,
          onAdjustAssumptions: () => {
            this.$fullscreenModal.closeAll();
            this.openSettings();
          },
        },
      );
    },
  },
};
</script>
